import React, { useEffect, useRef } from "react";
import { usePage } from "@inertiajs/react";

const SharePostModal = ({
    isOpen,
    onClose,
    sharePostModalLink,
    sharePostMessage,
    copiedToClipboard,
    setCopiedToClipboard,
}) => {
    const modal = useRef(null);

    const { props: sharedProps } = usePage();
    const { appName, appUrl, twitterUsername } = sharedProps;
    const postUrl = `${appUrl}/posts/${sharePostModalLink}`;

    // Close on outside click
    useEffect(() => {
        if (!isOpen) return; // Ensure the event listener is only active when the modal is open

        const clickHandler = (event) => {
            if (!modal.current || modal.current.contains(event.target)) return;
            onClose();
        };
        document.addEventListener("mousedown", clickHandler);
        return () => document.removeEventListener("mousedown", clickHandler);
    }, [isOpen, onClose]);

    // Close on Esc key
    useEffect(() => {
        if (!isOpen) return; // Ensure the event listener is only active when the modal is open

        const keyHandler = (e) => {
            if (e.key === "Escape") onClose();
        };
        document.addEventListener("keydown", keyHandler);
        return () => document.removeEventListener("keydown", keyHandler);
    }, [isOpen, onClose]);

    const handleCopy = () => {
        navigator.clipboard.writeText(postUrl).then(() => {
            setCopiedToClipboard(true);
            setTimeout(() => setCopiedToClipboard(false), 2000);
        });
    };

    return (
        <div
            className={`fixed left-0 top-0 z-999999 flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5 ${
                isOpen ? "block" : "hidden"
            }`}
        >
            <div
                ref={modal}
                className="w-100 rounded-lg bg-white px-8 py-12 text-center dark:bg-boxdark"
            >
                <h3 className="pb-2 text-xl font-bold text-black dark:text-white sm:text-2xl">
                    Share This Post
                </h3>

                <div className="mt-6 mb-6">
                    <div className="flex items-center border rounded overflow-hidden ">
                        <button
                            onClick={handleCopy}
                            className="px-4 py-4 bg-gray-200 border-r hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 transition text-gray-800 dark:text-white"
                        >
                            {copiedToClipboard ? (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="15"
                                    height="15"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                >
                                    <path d="M9 16.17L4.83 12 3.41 13.41 9 19 21 7 19.59 5.59z" />
                                </svg>
                            ) : (
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="15"
                                    height="15"
                                    viewBox="0 0 24 24"
                                    fill="currentColor"
                                >
                                    <path d="M16 1H6c-1.1 0-2 .9-2 2v14h2V3h10V1zm3 4H10c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h9c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 18H10V7h9v16z" />
                                </svg>
                            )}
                        </button>
                        <input
                            type="text"
                            value={postUrl}
                            readOnly
                            className="w-full px-5 bg-gray py-3 text-black outline-none transition disabled:cursor-default dark:text-white border-stroke dark:focus:border-primary focus:border-primary active:border-primary dark:border-form-strokedark dark:bg-form-input"
                        />
                    </div>
                </div>

                <div className="flex justify-center gap-4 mb-6">
                    <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=${postUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:opacity-80"
                    >
                        <svg
                            width="52"
                            height="50"
                            viewBox="0 0 100 100"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle cx="48" cy="50" r="50" fill="#3b5998" />
                            <path
                                d="M58 33h-5c-1.657 0-3 1.343-3 3v6h8l-1 8h-7v20h-9V50h-6v-8h6v-6c0-5.523 4.477-10 10-10h7v9z"
                                fill="white"
                            />
                        </svg>
                    </a>
                    <a
                        href={`https://twitter.com/intent/tweet?via=${twitterUsername}&text=${encodeURIComponent(
                            sharePostMessage.slice(0, 182)
                        )}&url=${postUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sky-500 hover:opacity-80"
                    >
                        <svg
                            width="50"
                            height="50"
                            viewBox="0 0 100 100"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle cx="50" cy="50" r="50" fill="#1DA1F2" />
                            <path
                                d="M78 36.1c-2.2.97-4.6 1.63-7.1 1.93 2.6-1.55 4.6-4.02 5.5-6.96-2.4 1.4-5 2.4-7.7 2.95a12.2 12.2 0 0 0-20.8 11.1c-10.2-.5-19.2-5.4-25.2-12.8-3.3 5.7-1.6 13 4.2 16.8-2-.06-3.9-.6-5.6-1.5v.15c0 6.2 4.4 11.4 10.3 12.6a12.1 12.1 0 0 1-5.5.2 12.3 12.3 0 0 0 11.4 8.5c-5.6 4.4-12.5 6.2-20 5.4 6.1 3.9 13.3 6.2 21 6.2 25.2 0 39-20.9 39-39v-1.8a27.7 27.7 0 0 0 6.8-7z"
                                fill="white"
                            />
                        </svg>
                    </a>
                    <a
                        href={`https://www.linkedin.com/shareArticle?&url=${postUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-700 hover:opacity-80"
                    >
                        <svg
                            width="50"
                            height="50"
                            viewBox="0 0 100 100"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <circle cx="50" cy="50" r="50" fill="#0077B5" />
                            <path
                                d="M30 38h10v34H30V38zm5-17a6 6 0 1 1 0 12 6 6 0 0 1 0-12zm12 17h10v5h.14c1.4-2.5 5-5 10.36-5C77 38 80 44 80 52v20H70V54c0-4-1.5-6-4.7-6-2.6 0-4.2 1.7-4.9 3.3-.3.7-.3 1.6-.3 2.5v18h-10V38z"
                                fill="white"
                            />
                        </svg>
                    </a>
                </div>

                <p className="text-gray-600 text-sm">
                    Share <b>{appName}</b> with a Friend!
                </p>

                {/* <div className="mt-8 flex gap-4 justify-center">
                    <button
                        onClick={onClose}
                        className="rounded border border-stroke px-6 py-2 text-black dark:border-strokedark dark:text-white"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onClose}
                        className="rounded bg-primary px-6 py-2 text-white hover:bg-opacity-90"
                    >
                        Done
                    </button>
                </div> */}
            </div>
        </div>
    );
};

export default SharePostModal;
